/**
 * ConfigModals — IFC Category Selector modal (wraps already-extracted IFCCategorySelector) + Property Set configuration modal for SitesPage. IFCCategorySelector imported directly (same component).
 *
 * JSX moved verbatim from src/components/SitesPage.tsx (SitesPage split 3/3).
 * Rendered DOM is byte-equivalent (classNames, text, conditional structure, keys unchanged).
 * All page state/handlers arrive as props; the interface + SitesPage invocation are
 * generated from a single prop list so they cannot drift.
 */
import React, { lazy, Suspense } from 'react';
import ViewerErrorBoundary from '../../components/ViewerErrorBoundary';
import { Asset } from '../../services/AssetService';
import type { IFCElementInfo } from '../../services/IFCAnalysisService';

// P4 payload fix (2026-08): IFCCategorySelector value-imports IFCAnalysisService
// (-> web-ifc-three, ~716KB gz). ConfigModals is in the eager boot bundle via
// SitesPage, so the selector loads lazily when its modal actually opens.
const IFCCategorySelector = lazy(() => import(/* webpackChunkName: "ifc-category-selector" */ '../../components/IFCCategorySelector'));

interface ConfigModalsProps {
  generatingPOIs: boolean;
  ifcAnalysisResult: any | null;
  ifcUploadedFile: File | null;
  propertySetConfig: { availableSets: string[]; visibleSets: string[] | null } | null;
  propertySetSearch: string;
  selectedAsset: Asset | null;
  showIFCCategorySelector: boolean;
  showPropertySetModal: boolean;
  setPropertySetSearch: React.Dispatch<React.SetStateAction<string>>;
  setShowPropertySetModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleIFCCategoriesSelected: (selectedCategories: string[]) => Promise<void>;
  handleIFCCategoryCancelSelection: () => void;
  handleLocationsCreated: (spatialElements: IFCElementInfo[]) => void;
  handleSavePropertySetConfig: () => Promise<void>;
  handleSelectAllPropertySets: () => void;
  handleTogglePropertySet: (setName: string) => void;
  handleUnselectAllPropertySets: () => void;
}

const ConfigModals: React.FC<ConfigModalsProps> = ({
  generatingPOIs,
  ifcAnalysisResult,
  ifcUploadedFile,
  propertySetConfig,
  propertySetSearch,
  selectedAsset,
  showIFCCategorySelector,
  showPropertySetModal,
  setPropertySetSearch,
  setShowPropertySetModal,
  handleIFCCategoriesSelected,
  handleIFCCategoryCancelSelection,
  handleLocationsCreated,
  handleSavePropertySetConfig,
  handleSelectAllPropertySets,
  handleTogglePropertySet,
  handleUnselectAllPropertySets,
}) => {
  return (
    <>
      {/* IFC Category Selector Modal */}
      {showIFCCategorySelector && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '900px', width: '90vw' }}>
            <ViewerErrorBoundary kind="page"><Suspense fallback={<div style={{ padding: 24, textAlign: 'center' }}>Loading…</div>}>
              <IFCCategorySelector
                analysisResult={ifcAnalysisResult}
                onCategoriesSelected={handleIFCCategoriesSelected}
                onLocationsCreated={handleLocationsCreated}
                onCancel={handleIFCCategoryCancelSelection}
                loading={generatingPOIs}
                assetId={selectedAsset?.id || ''}
                sourceFile={ifcUploadedFile?.name}
              />
            </Suspense></ViewerErrorBoundary>
          </div>
        </div>
      )}

      {/* Property Set Configuration Modal */}
      {showPropertySetModal && propertySetConfig && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-content">
              <h2>Configure Property Sets</h2>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Select which property sets to display in the Element Info panel when viewing elements in the 3D viewer.
              </p>
              {/* Search Input */}
              <input
                type="text"
                placeholder="Search property sets..."
                value={propertySetSearch}
                onChange={(e) => setPropertySetSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  marginBottom: '12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--background-primary)',
                  color: 'var(--text-primary)'
                }}
              />
              {/* Select All / Unselect All Buttons */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <button
                  onClick={handleSelectAllPropertySets}
                  className="btn btn-secondary btn-small"
                >
                  Select All
                </button>
                <button
                  onClick={handleUnselectAllPropertySets}
                  className="btn btn-secondary btn-small"
                >
                  Unselect All
                </button>
              </div>
              <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '16px' }}>
                {propertySetConfig.availableSets
                  .filter(setName =>
                    setName.toLowerCase().includes(propertySetSearch.toLowerCase())
                  )
                  .map(setName => (
                  <label
                    key={setName}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      marginBottom: '4px',
                      backgroundColor: 'var(--background-secondary)'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={propertySetConfig.visibleSets === null ||
                               propertySetConfig.visibleSets.includes(setName)}
                      onChange={() => handleTogglePropertySet(setName)}
                      style={{ marginRight: '12px' }}
                    />
                    <span style={{ fontFamily: 'monospace' }}>{setName}</span>
                  </label>
                ))}
              </div>
              <div className="modal-actions">
                <button onClick={() => setShowPropertySetModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button onClick={handleSavePropertySetConfig} className="btn btn-primary">
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConfigModals;
